import React, { Suspense, lazy } from 'react';
import { ConfigProvider, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TimezoneProvider } from './contexts/TimezoneContext';
import LoginForm from './components/LoginForm';
import MainLayout from './layouts/MainLayout';
import Dashboard from './pages/Dashboard';
import ErrorBoundary from './components/ErrorBoundary';
import 'antd/dist/reset.css';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';

/**
 * 除了首屏真正需要的三个（登录页、外壳、仪表盘）以外，每个页面都单独成 chunk。
 *
 * 之前 14 个页面全是静态 import，打出来是**一个** 2.1MB 的 chunk：只想看登录页
 * 也得先下完图表库、日期选择器、颜色选择器、CSV 解析器。约 1.1MB（52%）是从
 * 登录页和仪表盘根本到不了的代码。
 *
 * 这里不需要动 webpack 配置 —— react-scripts 用的是默认的 `chunks: 'async'`，
 * 之前拆不开纯粹是因为代码里一个异步边界都没有。加上 lazy() 就会自动分包。
 *
 * Dashboard 故意保持静态：它是登录后的落地页，做成 lazy 只是给最常走的那条路
 * 多加一次往返。它也没有独占的重依赖（recharts 只在 CheckinQuota 和
 * ScheduledTasksManagement 里）。
 */
const CheckinQuota = lazy(() => import('./pages/CheckinQuota'));
const QuotaUsage = lazy(() => import('./pages/QuotaUsage'));
const GitHubAccountManagement = lazy(() => import('./pages/GitHubAccountManagement'));
const GitHubGroupsManagement = lazy(() => import('./pages/GitHubGroupsManagement'));
const RepositoryStarManagement = lazy(() => import('./pages/RepositoryStarManagement'));
const ScheduledTasksManagement = lazy(() => import('./pages/ScheduledTasksManagement'));
const ApiWebsiteManagement = lazy(() => import('./pages/ApiWebsiteManagement'));
const ApiPushSettings = lazy(() => import('./pages/ApiPushSettings'));
const MonitorSettings = lazy(() => import('./pages/MonitorSettings'));
const SolverSettings = lazy(() => import('./pages/SolverSettings'));
const NetworkSettings = lazy(() => import('./pages/NetworkSettings'));
const ProxySettings = lazy(() => import('./pages/ProxySettings'));
const OperationLogs = lazy(() => import('./pages/OperationLogs'));
const DisplaySettings = lazy(() => import('./pages/DisplaySettings'));

/** chunk 还在路上时占位。放在内容区里，侧边栏和顶栏保持可见。 */
const PageLoading: React.FC = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      minHeight: 280,
    }}
    role="status"
  >
    <Spin />
    {/* 文案不能用 Spin 的 tip：antd 只在 nested/fullscreen 形态下渲染 tip，
        单独用一个 <Spin tip="..."/> 时那段文字根本不会出现（还会打警告）。 */}
    <span style={{ color: '#667482', fontSize: 13 }}>正在加载页面…</span>
  </div>
);

/**
 * 每个页面单独包一层 error boundary，而不是整个应用只包一层：
 * 一处渲染错误只该毁掉那一页，侧边栏和其他页面必须还能用。
 *
 * Suspense 也按页放，理由一样：等某一页的 chunk 时，外壳不该跟着消失。
 */
const page = (element: React.ReactNode) => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoading />}>{element}</Suspense>
  </ErrorBoundary>
);

const AppContent: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 14,
        height: '100vh'
      }}>
        <Spin size="large" />
        {/* 同上：Spin 的 tip 在非 nested/fullscreen 形态下不会渲染，
            所以这句"加载中"原来是看不见的。 */}
        <span style={{ color: '#667482', fontSize: 13 }}>加载中…</span>
      </div>
    );
  }

  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        token: {
          colorPrimary: '#2563eb',
          colorSuccess: '#15935b',
          colorWarning: '#bd7410',
          colorError: '#d14343',
          colorInfo: '#2563eb',
          colorText: '#17212b',
          colorTextSecondary: '#667482',
          colorBorder: '#d7dee7',
          colorBgLayout: '#f6f8fb',
          borderRadius: 7,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Microsoft YaHei", sans-serif',
        },
        components: {
          Layout: {
            bodyBg: '#f6f8fb',
            headerBg: '#ffffff',
            siderBg: '#ffffff',
          },
          Menu: {
            itemSelectedBg: '#eff5ff',
            itemSelectedColor: '#2563eb',
            itemHoverBg: '#f4f7fb',
          },
          Table: {
            headerBg: '#f8fafc',
            rowHoverBg: '#fbfcfe',
          },
        },
      }}
    >
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginForm />
          }
        />
        <Route
          path="/"
          element={
            isAuthenticated ? <MainLayout /> : <Navigate to="/login" replace />
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={page(<Dashboard />)} />
          <Route path="checkin-quota" element={page(<CheckinQuota />)} />
          <Route path="quota-usage" element={page(<QuotaUsage />)} />
          <Route path="github/accounts" element={page(<GitHubAccountManagement />)} />
          <Route path="github/groups" element={page(<GitHubGroupsManagement />)} />
          <Route path="github/repositories" element={page(<RepositoryStarManagement />)} />
          <Route path="automation/tasks" element={page(<ScheduledTasksManagement />)} />
          <Route path="automation/websites" element={page(<ApiWebsiteManagement />)} />
          <Route path="automation/push-settings" element={page(<ApiPushSettings />)} />
          <Route path="settings/monitor" element={page(<MonitorSettings />)} />
          <Route path="settings/solver" element={page(<SolverSettings />)} />
          <Route path="settings/network" element={page(<NetworkSettings />)} />
          <Route path="settings/proxy" element={page(<ProxySettings />)} />
          <Route path="settings/display" element={page(<DisplaySettings />)} />
          <Route path="logs" element={page(<OperationLogs />)} />
        </Route>
        <Route
          path="*"
          element={
            <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />
          }
        />
      </Routes>
    </ConfigProvider>
  );
};

const App: React.FC = () => {
  return (
    <TimezoneProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </TimezoneProvider>
  );
};

export default App;
